/* eslint-disable @typescript-eslint/promise-function-async */
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticationGuard } from './core';
import { RESET_PASSWORD_GUID } from './core/constants/route-params-name';
import { CookiesPolicyComponent } from './core/pages/cookies-policy/cookies-policy.component';
import { DeviceInfoPage } from './core/pages/device-info/device-info.page';
import { ExpiredPasswordPage } from './core/pages/expired-password/expired-password.page';
import { ForgottenPasswordPage } from './core/pages/forgotten-password/forgotten-password.page';
import { JunkEmailsComponent } from './core/pages/junk-emails/junk-emails.component';
import { LoginPage } from './core/pages/login/login.page';
import { LogoutPage } from './core/pages/logout/logout.page';
import { PrivacyPolicyComponent } from './core/pages/privacy-policy/privacy-policy.component';
import { RedirectFromOldAppPage } from './core/pages/redirect-from-old-app/redirect-from-old-app.page';
import { ResetForgottenPasswordPage } from './core/pages/reset-forgotten-password/reset-forgotten-password.page';
import { SessionClosedPage } from './core/pages/session-closed/session-closed.page';
import { SystemUnavailablePage } from './core/pages/system-unavailable/system-unavailable.page';
import { UnauthorizedPage } from './core/pages/unauthorized/unauthorized.page';
import { UnsupportedDevicePage } from './core/pages/unsupported-device/unsupported-device.page';
import { WritingBusinessPage } from './core/pages/writing-business/writing-business.page';
import { States } from './shared';

const routes: Routes = [
  {
    path: ':host',
    children: [
      {
        path: States.Login,
        component: LoginPage
      },
      {
        path: States.Logout,
        component: LogoutPage
      },
      {
        path: States.ForgottenPassword,
        component: ForgottenPasswordPage
      },
      {
        path: `${States.ResetForgottenPassword}/:${RESET_PASSWORD_GUID}`,
        component: ResetForgottenPasswordPage
      },
      {
        path: States.ExpiredPassword,
        component: ExpiredPasswordPage
      },
      {
        path: States.PrivacyPolicy,
        component: PrivacyPolicyComponent
      },
      {
        path: States.JunkEmails,
        component: JunkEmailsComponent
      },
      {
        path: States.CookiesPolicy,
        component: CookiesPolicyComponent
      },
      {
        path: States.AccessController,
        loadChildren: () =>
          import('./interfaceTypes/broker/access-controller/access-controller.module').then(
            (m) => m.AccessControllerModule
          ),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.BrokerHelpCenter,
        loadChildren: () =>
          import('./interfaceTypes/broker/help-center/broker-help-center.module').then((m) => m.BrokerHelpCenterModule)
      },
      {
        path: States.CustomerHelpCenter,
        loadChildren: () =>
          import('./interfaceTypes/direct/help-center/customer-help-center.module').then(
            (m) => m.CustomerHelpCenterModule
          )
      },
      {
        path: States.BrokerCaseHub,
        loadChildren: () =>
          import('./interfaceTypes/broker/broker-case-hub/broker-case-hub.module').then((m) => m.BrokerCaseHubModule),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.Agent,
        loadChildren: () =>
          import('./interfaceTypes/broker/agent-registration/agent-registration.module').then(
            (m) => m.AgentRegistrationModule
          )
      },
      {
        path: States.DirectActivation,
        loadChildren: () =>
          import('./interfaceTypes/direct/direct-activation/direct-activation.module').then(
            (m) => m.DirectActivationModule
          )
      },
      {
        path: States.BrokerCaseOverview,
        loadChildren: () =>
          import('./interfaceTypes/broker/case-overview/case-overview.module').then((m) => m.CaseOverviewModule),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.BrokerProfile,
        loadChildren: () =>
          import('./interfaceTypes/broker/broker-user-profile/broker-user-profile.module').then(
            (m) => m.BrokerUserProfileModule
          ),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.Application,
        loadChildren: () =>
          import('./interfaceTypes/broker/mortgage-application/mortgage-application.module').then(
            (m) => m.MortgageApplicationModule
          ),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.Kfi,
        loadChildren: () => import('./interfaceTypes/broker/kfi/kfi.module').then((m) => m.KfiModule),
        canActivate: [AuthenticationGuard]
      },
      { path: 'demo', loadChildren: () => import('./demo/demo.module').then((m) => m.DemoModule) },
      { path: States.MakePayment, redirectTo: `${States.MakePayment}/`, pathMatch: 'full' },
      {
        path: `${States.MakePayment}/:paymentType`,
        loadChildren: () =>
          import('./interfaceTypes/onlinePayments/online-payments.module').then((m) => m.OnlinePaymentsModule)
      },
      {
        path: States.Switch,
        loadChildren: () =>
          import('./interfaceTypes/broker/existing-business/existing-business.module').then(
            (m) => m.ExistingBusinessModule
          ),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.CustomerSwitch,
        loadChildren: () =>
          import('./interfaceTypes/direct/customer-switch/customer-switch.module').then((m) => m.CustomerSwitchModule),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.DirectProfile,
        loadChildren: () =>
          import('./interfaceTypes/direct/direct-user-profile/direct-user-profile.module').then(
            (m) => m.DirectUserProfileModule
          ),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.NonAdvised,
        loadChildren: () =>
          import('./interfaceTypes/direct/landing/non-advised/non-advised.module').then((m) => m.NonAdvisedModule)
      },
      {
        path: States.Advised,
        loadChildren: () =>
          import('./interfaceTypes/direct/landing/advised/advised.module').then((m) => m.AdvisedModule)
      },
      {
        path: States.Customer,
        loadChildren: () =>
          import('./interfaceTypes/direct/existing-customer-registration/existing-customer-registration.module').then(
            (m) => m.ExistingCustomerRegistrationModule
          )
      },
      {
        path: States.OnlineOffer,
        loadChildren: () =>
          import('./interfaceTypes/direct/online-offer/online-offer.module').then((m) => m.OnlineOfferModule)
      },
      {
        path: States.CustomerApplication,
        loadChildren: () =>
          import('./interfaceTypes/direct/customer-mortgage-application/customer-mortgage-application.module').then(
            (m) => m.CustomerMortgageApplicationModule
          ),
        canActivate: [AuthenticationGuard]
      },
      { path: States.SessionClosed, component: SessionClosedPage },
      { path: States.NoCaseAccess, component: UnauthorizedPage },
      {
        path: States.CustomerHub,
        loadChildren: () =>
          import('./interfaceTypes/direct/customer-hub/customer-hub.module').then((m) => m.CustomerHubModule),
        canActivate: [AuthenticationGuard]
      },
      {
        path: States.UnsupportedDevice,
        component: UnsupportedDevicePage
      },
      {
        path: States.DeviceInfo,
        component: DeviceInfoPage
      },
      {
        path: States.SystemUnavailable,
        component: SystemUnavailablePage
      },
      {
        path: States.Redirect,
        component: RedirectFromOldAppPage
      },
      {
        path: `${States.WritingBusiness}/:agentId`,
        component: WritingBusinessPage,
        canActivate: [AuthenticationGuard]
      }
    ]
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
